/**
 * Endings: the drift map (per participant) and the rhizome (collective).
 *
 * Drift Map — each participant receives a thin map of their trajectory: what fed
 * them and what they fed, by rhetorical tactic. Rhizome — the collective layer:
 * which fragments thrived, which died, who passed what to whom.
 */
import { TACTICS } from './model.js';
function normalise(dwell) {
    const total = TACTICS.reduce((s, t) => s + dwell[t], 0);
    const share = {};
    for (const t of TACTICS)
        share[t] = total > 0 ? dwell[t] / total : 0;
    return share;
}
export function computeDrift(session) {
    const share = normalise(session.tacticDwell);
    let dominant = null;
    let max = 0;
    for (const t of TACTICS) {
        if (session.tacticDwell[t] > max) {
            max = session.tacticDwell[t];
            dominant = t;
        }
    }
    const pct = dominant ? Math.round(share[dominant] * 100) : 0;
    const summary = dominant
        ? `Your attention drifted toward ${dominant} (${pct}% of the time you held).`
        : 'You held nothing long enough to leave a trace.';
    return {
        sessionId: session.id,
        tacticDwell: { ...session.tacticDwell },
        tacticShare: share,
        dominant,
        fedToYou: [...session.seen],
        youFed: [...session.fed],
        summary,
    };
}
/**
 * Build the rhizome from a flat set of fragments. Edges are derived from each
 * fragment's `parents`. Nodes with missing parents (pruned/decayed away) simply
 * have fewer edges — the graph tolerates gaps.
 */
export function buildRhizome(fragments) {
    const byId = new Map(fragments.map((f) => [f.id, f]));
    const edges = [];
    for (const f of fragments) {
        for (const p of f.parents) {
            if (byId.has(p)) {
                edges.push({ from: p, to: f.id, op: f.mutatedBy });
            }
        }
    }
    return { nodes: fragments, edges };
}
export function rhizomeStats(r) {
    const byTactic = { nostalgia: 0, fear: 0, identity: 0, authority: 0, doubt: 0 };
    let maxGeneration = 0;
    let thriving = 0;
    let dead = 0;
    let roots = 0;
    for (const f of r.nodes) {
        byTactic[f.tactic]++;
        if (f.generation > maxGeneration)
            maxGeneration = f.generation;
        if (f.vitality >= 0.55)
            thriving++;
        if (f.vitality < 0.2)
            dead++;
        if (f.parents.length === 0)
            roots++;
    }
    return { total: r.nodes.length, byTactic, maxGeneration, thriving, dead, roots };
}
//# sourceMappingURL=analysis.js.map